/**
 * Project storage.
 *
 * `Save` never downloads: the `.lakar` payload is written into the origin private file
 * system (OPFS), which is real storage owned by the app origin. Exporting a file is a
 * separate, explicit action used to move a project to another device.
 *
 * The directory access is injectable so the store can be unit-tested with an in-memory
 * handle, and so a linked PC folder can be used as an alternative target.
 */

export const PROJECT_EXTENSION = '.lakar';
const ROOT_DIR = 'lakaran';
const PROJECTS_DIR = 'projects';

export interface FsFileLike {
  name: string;
  size: number;
  lastModified: number;
  text(): Promise<string>;
}

export interface FsWritableLike {
  write(data: string): Promise<void>;
  close(): Promise<void>;
}

export interface FsFileHandleLike {
  kind: 'file';
  name: string;
  getFile(): Promise<FsFileLike>;
  createWritable(options?: { keepExistingData?: boolean }): Promise<FsWritableLike>;
  queryPermission?(descriptor: { mode: 'read' | 'readwrite' }): Promise<PermissionState>;
  requestPermission?(descriptor: { mode: 'read' | 'readwrite' }): Promise<PermissionState>;
}

export interface FsDirectoryHandleLike {
  kind: 'directory';
  name: string;
  values?(): AsyncIterable<FsFileHandleLike | FsDirectoryHandleLike>;
  entries?(): AsyncIterable<[string, FsFileHandleLike | FsDirectoryHandleLike]>;
  getFileHandle(name: string, options?: { create?: boolean }): Promise<FsFileHandleLike>;
  getDirectoryHandle(name: string, options?: { create?: boolean }): Promise<FsDirectoryHandleLike>;
  removeEntry(name: string, options?: { recursive?: boolean }): Promise<void>;
  queryPermission?(descriptor: { mode: 'read' | 'readwrite' }): Promise<PermissionState>;
  requestPermission?(descriptor: { mode: 'read' | 'readwrite' }): Promise<PermissionState>;
}

export interface StoredProject {
  name: string;
  size: number;
  modified: number;
}

export interface SaveOutcome {
  where: 'opfs';
  name: string;
  size: number;
  at: number;
}

let rootProvider: (() => Promise<FsDirectoryHandleLike>) | null = null;

/** Test/replacement hook: point the store at another directory (OPFS is the default). */
export function setStorageRootProvider(provider: (() => Promise<FsDirectoryHandleLike>) | null): void {
  rootProvider = provider;
}

function defaultRoot(): Promise<FsDirectoryHandleLike> {
  const storage = navigator.storage as StorageManager & {
    getDirectory?: () => Promise<FsDirectoryHandleLike>;
  };
  if (!storage?.getDirectory) {
    return Promise.reject(new Error('This browser has no private file system (OPFS).'));
  }
  return storage.getDirectory();
}

async function rootDirectory(): Promise<FsDirectoryHandleLike> {
  return rootProvider ? rootProvider() : defaultRoot();
}

async function projectsDirectory(create = true): Promise<FsDirectoryHandleLike> {
  const root = await rootDirectory();
  const appDir = await root.getDirectoryHandle(ROOT_DIR, { create });
  return appDir.getDirectoryHandle(PROJECTS_DIR, { create });
}

export async function storageAvailable(): Promise<boolean> {
  try {
    await projectsDirectory(false);
    return true;
  } catch {
    return false;
  }
}

// ------------------------------------------------------------------- names

const INVALID = /[\\/:*?"<>|\u0000-\u001f]/g;

/** Normalises a drawing name into a safe `.lakar` file name. */
export function projectFileName(name: string): string {
  const trimmed = name.trim().replace(INVALID, '-').replace(/\s+/g, ' ').slice(0, 96);
  const base = trimmed.length > 0 ? trimmed : 'untitled';
  return base.toLowerCase().endsWith(PROJECT_EXTENSION) ? base : `${base}${PROJECT_EXTENSION}`;
}

/** `part`, `part (2)`, `part (3)` … avoiding names already in `taken`. */
export function uniqueProjectName(name: string, taken: Iterable<string>): string {
  const fileName = projectFileName(name);
  const existing = new Set([...taken].map((entry) => entry.toLowerCase()));
  if (!existing.has(fileName.toLowerCase())) return fileName;

  const stem = fileName.slice(0, fileName.length - PROJECT_EXTENSION.length);
  for (let index = 2; index < 1000; index++) {
    const candidate = `${stem} (${index})${PROJECT_EXTENSION}`;
    if (!existing.has(candidate.toLowerCase())) return candidate;
  }
  return `${stem} (${Date.now()})${PROJECT_EXTENSION}`;
}

// ---------------------------------------------------------------- projects

export async function listProjects(): Promise<StoredProject[]> {
  let dir: FsDirectoryHandleLike;
  try {
    dir = await projectsDirectory(false);
  } catch {
    return [];
  }

  const out: StoredProject[] = [];
  const entries = dir.entries?.() ?? iterateValues(dir);
  for await (const [name, handle] of entries) {
    if (handle.kind !== 'file' || !name.toLowerCase().endsWith(PROJECT_EXTENSION)) continue;
    try {
      const file = await handle.getFile();
      out.push({ name, size: file.size, modified: file.lastModified });
    } catch {
      out.push({ name, size: 0, modified: 0 });
    }
  }
  return out.sort((a, b) => b.modified - a.modified);
}

async function* iterateValues(
  dir: FsDirectoryHandleLike,
): AsyncGenerator<[string, FsFileHandleLike | FsDirectoryHandleLike]> {
  if (!dir.values) return;
  for await (const handle of dir.values()) {
    yield [handle.name, handle];
  }
}

export async function readProject(name: string): Promise<string> {
  const dir = await projectsDirectory(false);
  const handle = await dir.getFileHandle(name);
  const file = await handle.getFile();
  return file.text();
}

export async function writeProject(name: string, text: string): Promise<SaveOutcome> {
  const dir = await projectsDirectory(true);
  const handle = await dir.getFileHandle(projectFileName(name), { create: true });
  const writable = await handle.createWritable();
  try {
    await writable.write(text);
  } finally {
    await writable.close();
  }
  const file = await handle.getFile();
  return { where: 'opfs', name: file.name, size: file.size, at: Date.now() };
}

export async function deleteProject(name: string): Promise<void> {
  const dir = await projectsDirectory(true);
  await dir.removeEntry(name);
}

/**
 * Renames a stored project by writing the payload under the new name and deleting the old
 * entry — the file-system handles expose no portable `move`.
 */
export async function renameProject(from: string, to: string): Promise<SaveOutcome> {
  const dir = await projectsDirectory(true);
  const target = projectFileName(to);
  const text = await (await dir.getFileHandle(from)).getFile();
  const payload = await text.text();

  const handle = await dir.getFileHandle(target, { create: true });
  const writable = await handle.createWritable();
  try {
    await writable.write(payload);
  } finally {
    await writable.close();
  }
  if (target !== from) await dir.removeEntry(from);
  const file = await handle.getFile();
  return { where: 'opfs', name: file.name, size: file.size, at: Date.now() };
}

export async function projectExists(name: string): Promise<boolean> {
  const projects = await listProjects();
  return projects.some((project) => project.name.toLowerCase() === name.toLowerCase());
}

// ------------------------------------------------------------- persistence

export type PersistenceState = 'persistent' | 'best-effort' | 'unknown';

export async function persistenceState(): Promise<PersistenceState> {
  const storage = navigator.storage;
  if (!storage?.persisted) return 'unknown';
  try {
    return (await storage.persisted()) ? 'persistent' : 'best-effort';
  } catch {
    return 'unknown';
  }
}

/** Asks the browser to keep OPFS around instead of evicting it under pressure. */
export async function requestPersistence(): Promise<PersistenceState> {
  const storage = navigator.storage;
  if (storage?.persist) {
    try {
      await storage.persist();
    } catch {
      /* best effort */
    }
  }
  return persistenceState();
}
